/**
 * Sector operations — Construction Issue Photos Service (R39 B5.p, SO-P260).
 * Uploads photos directly to Firebase Storage under workspaces/{workspaceId}/con_issues/{issueId}/{uid}/{fileName}.
 */

import { ref, uploadBytes } from 'firebase/storage'
import { firebaseStorage, firebaseAuth } from './firebase'
import { getNlsString } from '../nls/nlsRuntime'
import { createOpId } from './helper/opId'
import {
  CON_ISSUE_PHOTO_CONTENT_TYPES,
  MAX_CON_ISSUE_PHOTO_BYTES,
} from '../types/ops'

export const ALLOWED_CON_ISSUE_PHOTO_MIME_TYPES = CON_ISSUE_PHOTO_CONTENT_TYPES
export { MAX_CON_ISSUE_PHOTO_BYTES }

function getSafeExtension(file: File): string {
  if (file.name.includes('.')) {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext && /^[a-z0-9]+$/.test(ext)) {
      return ext
    }
  }
  switch (file.type) {
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
    case 'image/heic':
      return 'heic'
    default:
      return 'jpg'
  }
}

export interface UploadConIssuePhotoResult {
  path: string
}

export async function uploadConIssuePhoto(
  workspaceId: string,
  issueId: string,
  file: File,
): Promise<UploadConIssuePhotoResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!issueId) throw new Error('issueId is required.')

  if (
    !ALLOWED_CON_ISSUE_PHOTO_MIME_TYPES.includes(
      file.type as (typeof ALLOWED_CON_ISSUE_PHOTO_MIME_TYPES)[number],
    )
  ) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_CON_ISSUE_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const fileId = `${createOpId('con_photo')}.${ext}`
  const path = `workspaces/${workspaceId}/con_issues/${issueId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  return { path }
}
